import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock, Download, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const DietDownload = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || searchParams.get('session_id') || '';
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(Boolean(token));

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/diets/download/${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok)
          throw new Error(
            data.detail || t('dietDownload.verifyError', 'Could not verify your download link.')
          );
        setInfo(data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token, t]);

  return (
    <div className="min-h-[75vh] flex items-center justify-center px-4 py-16 bg-gray-50/50">
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 max-w-lg w-full p-8 sm:p-10 text-center">
        {loading ? (
          <p className="text-gray-500 py-8">
            {t('dietDownload.preparing', 'Preparing your diet plan download…')}
          </p>
        ) : error ? (
          <>
            <div className="w-16 h-16 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-5">
              <Clock className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('dietDownload.unavailableTitle', 'Download Link Unavailable')}
            </h1>
            <p className="text-gray-600 text-sm mb-6">{error}</p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-primary text-white font-semibold px-6 py-3 rounded-full"
            >
              {t('dietDownload.contactSupport', 'Contact Support')}
            </Link>
          </>
        ) : info?.status === 'ready' ? (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('dietDownload.thankYouTitle', 'Thank You for Your Purchase!')}
            </h1>
            <p className="text-gray-600 text-sm mb-6">
              {t('dietDownload.readyPrefix', 'Your guide')} <strong>{info.title}</strong>{' '}
              {t('dietDownload.readySuffix', 'is ready to download.')}
            </p>
            <a
              href={`${API_URL}${info.download_url}`}
              className="w-full inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-primary text-white font-bold px-6 py-4 rounded-full shadow-lg transition-colors"
            >
              <Download className="w-5 h-5" />
              {t('dietDownload.downloadBtn', 'Download PDF Now')}
            </a>
          </>
        ) : (
          <>
            <div className="w-16 h-16 rounded-full bg-blue-50 text-primary flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('dietDownload.orderConfirmed', 'Order Confirmed!')}
            </h1>
            <p className="text-gray-600 text-sm mb-6">
              {info?.message ||
                t(
                  'dietDownload.orderConfirmedDesc',
                  'Your diet plan purchase is confirmed. Paola will send the PDF to your email shortly.'
                )}
            </p>
          </>
        )}

        <div className="mt-8 pt-6 border-t border-gray-100">
          <Link
            to="/diets"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('dietDownload.backToDiets', 'Back to Diet Plans')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DietDownload;
